import Skeleton from '@material-ui/lab/Skeleton';
import { mdiFilter, mdiSort } from '@mdi/js';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import RedesignDialog, {
  RedesignDialogButton,
  RedesignDialogIconButton,
  useRedesignDialogColors,
} from 'components/RedesignDialog';
import { pantonFonts } from 'components/theme/common';

const SKELETON_ROWS = 6;
const SKELETON_WIDTHS = ['62%', '48%', '70%', '55%', '66%', '42%'];

const MESSAGE_TEXT = {
  fontFamily: pantonFonts.panton,
  fontSize: '0.875rem',
  lineHeight: 1.45,
};

/** The day's rows while they load: the time, the glyphs and the text. */
function ActivityLogRowsSkeleton() {
  const colors = useRedesignDialogColors();
  return (
    <div aria-busy="true" data-activity-log-skeleton="true">
      {SKELETON_WIDTHS.slice(0, SKELETON_ROWS).map((width) => (
        <div
          key={width}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 0',
            minHeight: 36,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <Skeleton variant="circle" width={20} height={20} />
          <Skeleton variant="text" width={40} />
          <Skeleton variant="text" width={width} />
        </div>
      ))}
    </div>
  );
}

/**
 * The day dialog (ActivityLogDialog) in the redesigned dialog, for a user
 * who has the redesign on, headed as the project's day dialog
 * (ProjectActivityDialog): the user's name as the title, the weekday, the
 * date and the events shown under it (formatActivityDaySubtitle), the filter
 * and the sort before the close button; skeleton rows while the day loads,
 * the error with "Yritä uudelleen". The rows (`children`) and the
 * filter and sort menus (`menus`) are the dialog's own.
 */
function ActivityLogDialogRedesign({
  open,
  onClose,
  title,
  subtitle,
  loading,
  error,
  onRetry,
  hasItems,
  count,
  filterCount,
  onOpenFilter,
  onOpenSort,
  menus,
  children,
}) {
  const { t } = useTranslation('common');
  const colors = useRedesignDialogColors();
  const showTools = !loading && !error && hasItems;
  const filterLabel = t('activity_log.filter', 'Suodata');

  let body;
  if (loading) {
    body = <ActivityLogRowsSkeleton />;
  } else if (error) {
    body = (
      <div
        role="alert"
        style={{ padding: '24px 0', textAlign: 'center' }}
        data-activity-log-error="true"
      >
        <div style={{ ...MESSAGE_TEXT, color: colors.danger }}>
          {t('activity_log.load_error', 'Aktiviteettilokin lataus epäonnistui')}
        </div>
        <div style={{ marginTop: 12 }}>
          <RedesignDialogButton kind="secondary" size="small" onClick={onRetry}>
            {t('common.retry', 'Yritä uudelleen')}
          </RedesignDialogButton>
        </div>
      </div>
    );
  } else {
    body = (
      <>
        {count > 0 ? (
          children
        ) : (
          <div
            style={{ ...MESSAGE_TEXT, color: colors.muted, padding: '24px 0' }}
          >
            {t('activity_log.no_logs', 'Ei tapahtumia tältä päivältä')}
          </div>
        )}
      </>
    );
  }

  return (
    <RedesignDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      size="md"
      fullHeight
      headerActions={
        showTools ? (
          <>
            <RedesignDialogIconButton
              icon={mdiFilter}
              iconSize={1}
              aria-label={
                filterCount > 0
                  ? `${filterLabel} (${filterCount})`
                  : filterLabel
              }
              onClick={onOpenFilter}
              style={filterCount > 0 ? { color: colors.accent } : undefined}
              data-activity-log-filter={filterCount}
            />
            <RedesignDialogIconButton
              icon={mdiSort}
              iconSize={1}
              aria-label={t('activity_log.sort', 'Järjestä')}
              onClick={onOpenSort}
            />
          </>
        ) : null
      }
      data-activity-log-dialog="redesign"
    >
      {menus}
      {body}
    </RedesignDialog>
  );
}

ActivityLogDialogRedesign.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  loading: PropTypes.bool,
  error: PropTypes.any,
  onRetry: PropTypes.func.isRequired,
  /** The day has rows, whatever the filter shows of them. */
  hasItems: PropTypes.bool,
  /** The rows the filter shows (the body says when there are none). */
  count: PropTypes.number.isRequired,
  filterCount: PropTypes.number.isRequired,
  onOpenFilter: PropTypes.func.isRequired,
  onOpenSort: PropTypes.func.isRequired,
  menus: PropTypes.node,
  children: PropTypes.node,
};

export default ActivityLogDialogRedesign;
